import { useEffect, useState } from "react";
import { Loader2, Package, Plus, Pencil, Trash2, AlertTriangle, History, ArrowDownCircle, ArrowUpCircle, Upload, FileSpreadsheet, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import api, { formatApiError } from "../api";
import { BulkImportDialog, downloadMasterExcel } from "../components/BulkImportDialog";

const KATEGORI_BARANG = [
  "Ban", "Oli", "Filter", "Sparepart Mesin",
  "Suspension", "Bearing", "CAB & Body", "Transmision", "Brake", "Axle", "Cooling",
  "Clutch", "Intake & Exhaust", "Emission Control", "Tools", "Frame", "Propeller Shaft",
  "Lain-lain",
];
const fmtRp = (v) => `Rp ${Number(v || 0).toLocaleString("id-ID")}`;
const fmtTgl = (v) => {
  if (!v) return "—";
  const d = new Date(v + "T00:00:00");
  return isNaN(d) ? v : d.toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" });
};
const prediksiBadge = {
  Kritis: "bg-red-100 text-red-800 border border-red-200",
  Perhatian: "bg-amber-100 text-amber-800 border border-amber-200",
  Aman: "bg-emerald-100 text-emerald-800 border border-emerald-200",
  "Belum Ada Pola": "bg-zinc-100 text-zinc-600 border border-zinc-200",
};
const emptyForm = { Kode_Barang: "", Nama_Barang: "", Kategori_Barang: "", Harga_Beli_Satuan: "", Stok_Tersedia: "", Batas_Minimum_Stok: "" };

const BarangDialog = ({ open, onClose, editing, onSaved }) => {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(editing ? {
        Kode_Barang: editing.Kode_Barang, Nama_Barang: editing.Nama_Barang,
        Kategori_Barang: editing.Kategori_Barang, Harga_Beli_Satuan: String(editing.Harga_Beli_Satuan),
        Stok_Tersedia: String(editing.Stok_Tersedia), Batas_Minimum_Stok: String(editing.Batas_Minimum_Stok),
      } : emptyForm);
    }
  }, [open, editing]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.Kode_Barang || !form.Nama_Barang || !form.Kategori_Barang || form.Harga_Beli_Satuan === "" || form.Stok_Tersedia === "" || form.Batas_Minimum_Stok === "") {
      toast.error("Semua kolom wajib diisi");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        Harga_Beli_Satuan: parseFloat(form.Harga_Beli_Satuan),
        Stok_Tersedia: parseInt(form.Stok_Tersedia, 10),
        Batas_Minimum_Stok: parseInt(form.Batas_Minimum_Stok, 10),
      };
      if (editing) await api.put(`/gudang/${editing.id}`, payload);
      else await api.post("/gudang", payload);
      toast.success(editing ? "Barang diperbarui" : "Barang ditambahkan");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="barang-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{editing ? `Edit ${editing.Kode_Barang}` : "Tambah Suku Cadang"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Kode Barang</Label>
              <Input data-testid="barang-kode-input" placeholder="cth: BAN-001" value={form.Kode_Barang}
                onChange={(e) => set("Kode_Barang")(e.target.value.toUpperCase())} />
            </div>
            <div className="space-y-1.5">
              <Label>Kategori</Label>
              <Select value={form.Kategori_Barang} onValueChange={set("Kategori_Barang")}>
                <SelectTrigger data-testid="barang-kategori-select"><SelectValue placeholder="Pilih kategori" /></SelectTrigger>
                <SelectContent>
                  {KATEGORI_BARANG.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Nama Barang</Label>
            <Input data-testid="barang-nama-input" placeholder="cth: Ban Bridgestone 1000-20" value={form.Nama_Barang}
              onChange={(e) => set("Nama_Barang")(e.target.value)} />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label>Harga Beli (Rp)</Label>
              <Input data-testid="barang-harga-input" type="number" inputMode="numeric" min="0" step="0.01" value={form.Harga_Beli_Satuan}
                onChange={(e) => set("Harga_Beli_Satuan")(e.target.value)} className="input-touch num-hc text-zinc-900" />
            </div>
            <div className="space-y-1.5">
              <Label>Stok Tersedia</Label>
              <Input data-testid="barang-stok-input" type="number" inputMode="numeric" min="0" step="1" value={form.Stok_Tersedia}
                onChange={(e) => set("Stok_Tersedia")(e.target.value)} className="input-touch num-hc text-zinc-900" />
            </div>
            <div className="space-y-1.5">
              <Label>Batas Min. Stok</Label>
              <Input data-testid="barang-batas-input" type="number" inputMode="numeric" min="0" step="1" value={form.Batas_Minimum_Stok}
                onChange={(e) => set("Batas_Minimum_Stok")(e.target.value)} className="input-touch num-hc text-zinc-900" />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="barang-cancel-button" type="button" variant="outline" onClick={onClose} className="btn-touch">Batal</Button>
            <Button data-testid="barang-submit-button" type="submit" disabled={saving}
              className="bg-emerald-700 hover:bg-emerald-800 text-white transition-colors duration-200 btn-touch">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editing ? "Simpan Perubahan" : "Simpan"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const fmtWaktu = (iso) => {
  const d = new Date(iso);
  return isNaN(d) ? iso : d.toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const KartuStokDialog = ({ target, onClose }) => {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    const params = target !== "semua" ? { barang_id: target.id } : {};
    api.get("/gudang/kartu-stok", { params }).then(({ data }) => setRows(data)).catch(() => setRows([]));
  }, [target]);

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="kartu-stok-dialog" className="sm:max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Kartu Stok {target === "semua" ? "— Semua Barang" : `— ${target.Kode_Barang} (${target.Nama_Barang})`}
          </DialogTitle>
        </DialogHeader>
        {rows === null ? (
          <div className="flex items-center justify-center py-10 text-zinc-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : rows.length === 0 ? (
          <p data-testid="kartu-stok-empty" className="py-8 text-center text-sm text-zinc-400">Belum ada pergerakan stok tercatat</p>
        ) : (
          <table className="w-full text-sm" data-testid="kartu-stok-table">
            <thead>
              <tr className="border-y border-zinc-200 bg-zinc-50">
                {["Waktu", ...(target === "semua" ? ["Barang"] : []), "Jenis", "Jumlah", "Stok", "Sumber", "Oleh"].map((h) => (
                  <th key={h} className="text-left px-3 py-2 text-xs uppercase tracking-wider text-zinc-500 font-medium whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`kartu-stok-row-${r.id}`} className="border-b border-zinc-100">
                  <td className="px-3 py-2 whitespace-nowrap text-zinc-600">{fmtWaktu(r.waktu)}</td>
                  {target === "semua" && <td className="px-3 py-2 whitespace-nowrap font-medium">{r.Kode_Barang}</td>}
                  <td className="px-3 py-2 whitespace-nowrap">
                    <Badge className={`font-medium ${r.jenis === "Masuk" ? "bg-emerald-100 text-emerald-800 border border-emerald-200" : "bg-red-100 text-red-800 border border-red-200"}`}>
                      {r.jenis === "Masuk" ? <ArrowDownCircle className="w-3 h-3 mr-1" /> : <ArrowUpCircle className="w-3 h-3 mr-1" />}
                      {r.jenis}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 tabular-nums font-semibold">{r.jenis === "Masuk" ? "+" : "-"}{r.jumlah}</td>
                  <td className="px-3 py-2 tabular-nums whitespace-nowrap text-zinc-600">{r.stok_sebelum} → <b className="text-zinc-900">{r.stok_sesudah}</b></td>
                  <td className="px-3 py-2 whitespace-nowrap">{r.sumber}{r.ref && r.sumber !== "Stok Awal" && r.sumber !== "Penyesuaian Manual" ? ` (${r.ref})` : ""}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-xs text-zinc-500">{r.user_email}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default function GudangPage() {
  const [items, setItems] = useState(null);
  const [prediksi, setPrediksi] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [kartuTarget, setKartuTarget] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [filterKategori, setFilterKategori] = useState("semua");
  const [cari, setCari] = useState("");

  const load = () => {
    api.get("/gudang").then(({ data }) => setItems(data)).catch(() => setItems([]));
    api.get("/gudang/prediksi").then(({ data }) => setPrediksi(data)).catch(() => setPrediksi({ items: [] }));
  };
  useEffect(() => { load(); }, []);

  const remove = async (item) => {
    if (!window.confirm(`Hapus ${item.Kode_Barang} — ${item.Nama_Barang}?`)) return;
    try {
      await api.delete(`/gudang/${item.id}`);
      toast.success("Barang dihapus");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const restockItems = (items || []).filter((i) => i.restock_due);
  const shown = (items || []).filter((i) =>
    (filterKategori === "semua" || i.Kategori_Barang === filterKategori) &&
    (!cari || `${i.Kode_Barang} ${i.Nama_Barang}`.toLowerCase().includes(cari.toLowerCase())));

  return (
    <div data-testid="gudang-page" className="space-y-6 max-w-6xl">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-900 flex items-center gap-2">
            <Package className="w-7 h-7 text-emerald-700" /> Inventaris Gudang
          </h1>
          <p className="text-sm text-zinc-500 mt-1">Master suku cadang armada — baris merah berarti stok menyentuh batas minimum</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button data-testid="export-excel-button-gudang" variant="outline" onClick={() => downloadMasterExcel("gudang", "Master_Suku_Cadang.xlsx")}
            className="border-zinc-300 text-zinc-700 hover:bg-zinc-50 transition-colors duration-200">
            <FileSpreadsheet className="w-4 h-4 mr-2" /> Unduh Excel
          </Button>
          <Button data-testid="bulk-import-button-gudang" variant="outline" onClick={() => setImportOpen(true)}
            className="border-emerald-300 text-emerald-800 hover:bg-emerald-50 transition-colors duration-200">
            <Upload className="w-4 h-4 mr-2" /> Upload CSV/Excel
          </Button>
          <Button data-testid="kartu-stok-semua-button" variant="outline" onClick={() => setKartuTarget("semua")}
            className="border-emerald-300 text-emerald-800 hover:bg-emerald-50 transition-colors duration-200">
            <History className="w-4 h-4 mr-2" /> Kartu Stok
          </Button>
          <Button data-testid="add-barang-button" onClick={() => { setEditing(null); setDialogOpen(true); }}
            className="bg-emerald-700 hover:bg-emerald-800 text-white transition-colors duration-200">
            <Plus className="w-4 h-4 mr-2" /> Tambah Barang
          </Button>
        </div>
      </div>

      {restockItems.length > 0 && (
        <div data-testid="gudang-restock-alert" className="bg-red-50 border border-red-300 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-bold text-red-800">WAKTUNYA RESTOCK — {restockItems.length} barang menyentuh batas minimum stok</p>
            <p className="text-sm text-red-700 mt-0.5">{restockItems.map((i) => `${i.Nama_Barang} (sisa ${i.Stok_Tersedia})`).join(", ")}</p>
          </div>
        </div>
      )}

      <div className="bg-white border border-zinc-200 rounded-lg shadow-sm overflow-hidden" data-testid="prediksi-restock-card">
        <div className="px-5 py-4 border-b border-zinc-200">
          <p className="text-base font-semibold tracking-tight text-zinc-900 flex items-center gap-2">
            <CalendarClock className="w-4 h-4 text-emerald-700" /> Prediksi Restock
          </p>
          <p className="text-xs text-zinc-500 mt-0.5">Perkiraan kapan stok habis berdasarkan pola pemakaian {prediksi?.periode_hari || 90} hari terakhir (servis + pemakaian langsung truk)</p>
        </div>
        {prediksi === null ? (
          <div className="flex items-center justify-center py-10 text-zinc-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : prediksi.items.length === 0 ? (
          <p data-testid="prediksi-empty" className="px-5 py-8 text-center text-sm text-zinc-400">Belum ada barang di gudang.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="prediksi-table">
              <thead>
                <tr className="border-b border-zinc-200 bg-zinc-50">
                  {["Barang", "Stok", "Pemakaian 90 Hari", "Rata²/Bulan", "Sisa Hari s/d Batas Min", "Perkiraan Habis", "Status"].map((h) => (
                    <th key={h} className="text-left px-4 py-2.5 text-xs uppercase tracking-wider text-zinc-500 font-medium whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {prediksi.items.map((p) => (
                  <tr key={p.Kode_Barang} data-testid={`prediksi-row-${p.Kode_Barang}`}
                    className={`border-b border-zinc-100 transition-colors duration-200 ${p.status === "Kritis" ? "bg-red-50 hover:bg-red-100" : "hover:bg-zinc-50"}`}>
                    <td className="px-4 py-2.5 whitespace-nowrap"><span className="font-medium text-zinc-900">{p.Kode_Barang}</span> — {p.Nama_Barang}</td>
                    <td className="px-4 py-2.5 tabular-nums">{p.Stok_Tersedia}</td>
                    <td className="px-4 py-2.5 tabular-nums">{p.pemakaian_periode}</td>
                    <td className="px-4 py-2.5 tabular-nums">{p.rata_per_bulan}</td>
                    <td className="px-4 py-2.5 tabular-nums">{p.hari_hingga_batas_minimum == null ? "—" : `${p.hari_hingga_batas_minimum} hari`}</td>
                    <td className="px-4 py-2.5 whitespace-nowrap">{fmtTgl(p.perkiraan_habis)}</td>
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <Badge data-testid={`prediksi-status-${p.Kode_Barang}`} className={`${prediksiBadge[p.status] || ""} font-medium`}>{p.status}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg shadow-sm overflow-hidden">
        <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-zinc-200 bg-zinc-50/60">
          <Input data-testid="gudang-cari-input" value={cari} onChange={(e) => setCari(e.target.value)}
            placeholder="Cari kode / nama barang..." className="h-9 w-64 bg-white" />
          <Select value={filterKategori} onValueChange={setFilterKategori}>
            <SelectTrigger data-testid="gudang-filter-kategori" className="h-9 w-56 bg-white">
              <SelectValue placeholder="Semua Kategori" />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="semua">Semua Kategori</SelectItem>
              {KATEGORI_BARANG.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
            </SelectContent>
          </Select>
          {(filterKategori !== "semua" || cari) && (
            <Button data-testid="gudang-filter-reset" size="sm" variant="ghost" className="h-9 text-zinc-500"
              onClick={() => { setFilterKategori("semua"); setCari(""); }}>
              Reset
            </Button>
          )}
          <span data-testid="gudang-filter-count" className="ml-auto text-xs text-zinc-500">
            {shown.length} dari {(items || []).length} barang
          </span>
        </div>
        {items === null ? (
          <div className="flex items-center justify-center py-16 text-zinc-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : items.length === 0 ? (
          <p data-testid="gudang-empty" className="px-5 py-14 text-center text-sm text-zinc-400">
            Belum ada suku cadang. Klik "Tambah Barang" untuk mulai mencatat inventaris.
          </p>
        ) : shown.length === 0 ? (
          <p data-testid="gudang-filter-empty" className="px-5 py-14 text-center text-sm text-zinc-400">
            Tidak ada barang yang cocok dengan filter/pencarian.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="gudang-table">
              <thead>
                <tr className="border-b border-zinc-200 bg-zinc-50">
                  {["Kode", "Nama Barang", "Kategori", "Harga Beli Satuan", "Stok Tersedia", "Batas Minimum", "Status", "Aksi"].map((h) => (
                    <th key={h} className="text-left px-4 py-3 text-xs uppercase tracking-wider text-zinc-500 font-medium whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {shown.map((b) => (
                  <tr key={b.id} data-testid={`barang-row-${b.Kode_Barang}`}
                    className={`border-b border-zinc-100 transition-colors duration-200 ${b.restock_due ? "bg-red-50 hover:bg-red-100" : "hover:bg-zinc-50"}`}>
                    <td className="px-4 py-3 font-medium text-zinc-900 whitespace-nowrap">{b.Kode_Barang}</td>
                    <td className="px-4 py-3">{b.Nama_Barang}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <Badge className="bg-zinc-100 text-zinc-800 border border-zinc-200 font-medium">{b.Kategori_Barang}</Badge>
                    </td>
                    <td className="px-4 py-3 tabular-nums whitespace-nowrap">{fmtRp(b.Harga_Beli_Satuan)}</td>
                    <td className={`px-4 py-3 tabular-nums font-semibold ${b.restock_due ? "text-red-700" : "text-zinc-900"}`}>{b.Stok_Tersedia}</td>
                    <td className="px-4 py-3 tabular-nums">{b.Batas_Minimum_Stok}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <Badge data-testid={`barang-status-${b.Kode_Barang}`}
                        className={b.restock_due ? "bg-red-100 text-red-800 border border-red-200 font-medium" : "bg-emerald-100 text-emerald-800 border border-emerald-200 font-medium"}>
                        {b.restock_due ? "Waktunya Restock" : "Aman"}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <Button data-testid={`kartu-stok-${b.Kode_Barang}`} size="icon" variant="ghost" className="h-8 w-8 text-zinc-500 hover:text-emerald-700"
                          onClick={() => setKartuTarget(b)} title="Kartu stok barang ini">
                          <History className="w-3.5 h-3.5" />
                        </Button>
                        <Button data-testid={`edit-barang-${b.Kode_Barang}`} size="icon" variant="ghost" className="h-8 w-8 text-zinc-500 hover:text-emerald-700"
                          onClick={() => { setEditing(b); setDialogOpen(true); }}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button data-testid={`delete-barang-${b.Kode_Barang}`} size="icon" variant="ghost" className="h-8 w-8 text-zinc-500 hover:text-red-600"
                          onClick={() => remove(b)}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <BarangDialog open={dialogOpen} onClose={() => setDialogOpen(false)} editing={editing} onSaved={load} />
      <BulkImportDialog entity="gudang" open={importOpen} onClose={() => setImportOpen(false)} onDone={load} />
      {kartuTarget && <KartuStokDialog target={kartuTarget} onClose={() => setKartuTarget(null)} />}
    </div>
  );
}
